import type { EventDef } from "./types";

const FORMATTERS = new Map<string, Intl.DateTimeFormat>();
const FORMATTERS_MAX = 32;
const SLOT_CACHE = new Map<string, number[]>();
const SLOT_CACHE_MAX = 16;

export function formatterSize(): number {
  return FORMATTERS.size;
}

function formatter(tz: string): Intl.DateTimeFormat {
  const hit = FORMATTERS.get(tz);
  if (hit) {
    FORMATTERS.delete(tz);
    FORMATTERS.set(tz, hit);
    return hit;
  }
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  FORMATTERS.set(tz, fmt);
  if (FORMATTERS.size > FORMATTERS_MAX) {
    const first = FORMATTERS.keys().next().value;
    if (first !== undefined) FORMATTERS.delete(first);
  }
  return fmt;
}

export function zonedParts(
  epochMin: number,
  tz: string,
): { date: string; minutes: number; label: string } {
  const d = new Date(epochMin * 60_000);
  const map: Record<string, string> = {};
  for (const p of formatter(tz).formatToParts(d)) {
    if (p.type !== "literal") map[p.type] = p.value;
  }
  let hour = Number(map.hour ?? "0");
  if (hour === 24) hour = 0;
  const minute = Number(map.minute ?? "0");
  const minutes = hour * 60 + minute;
  const date = `${map.year}-${map.month}-${map.day}`;
  return { date, minutes, label: formatLabel(minutes) };
}

export function slotStarts(ev: EventDef): number[] {
  const key = `${ev.timezone}|${ev.dayStartMin}|${ev.dayEndMin}|${ev.slotMin}|${ev.dates.join(",")}`;
  const cached = SLOT_CACHE.get(key);
  if (cached) {
    SLOT_CACHE.delete(key);
    SLOT_CACHE.set(key, cached);
    return cached;
  }
  const out: number[] = [];
  const seen = new Set<number>();
  for (const date of ev.dates) {
    for (let m = ev.dayStartMin; m < ev.dayEndMin; m += ev.slotMin) {
      const epoch = localToEpochMin(date, m, ev.timezone);
      if (epoch === null) continue;
      if (seen.has(epoch)) continue;
      seen.add(epoch);
      out.push(epoch);
    }
  }
  Object.freeze(out);
  SLOT_CACHE.set(key, out);
  if (SLOT_CACHE.size > SLOT_CACHE_MAX) {
    const first = SLOT_CACHE.keys().next().value;
    if (first !== undefined) SLOT_CACHE.delete(first);
  }
  return out;
}

export function localToEpochMin(date: string, minutes: number, tz: string): number | null {
  const parts = date.split("-");
  const y = Number(parts[0]);
  const mo = Number(parts[1]);
  const d = Number(parts[2]);
  const hour = Math.floor(minutes / 60);
  const min = minutes % 60;
  const localAsUtc = Math.floor(Date.UTC(y, mo - 1, d, hour, min) / 60_000);
  const candidates = new Set<number>();
  let t = localAsUtc;
  for (let i = 0; i < 4; i++) {
    const off = offsetAt(t, tz);
    t = localAsUtc - off;
    candidates.add(t);
  }
  for (const base of [...candidates]) {
    candidates.add(base - 120);
    candidates.add(base - 60);
    candidates.add(base - 30);
    candidates.add(base + 30);
    candidates.add(base + 60);
    candidates.add(base + 120);
  }
  const matches: number[] = [];
  for (const c of candidates) {
    const p = zonedParts(c, tz);
    if (p.date === date && p.minutes === minutes) matches.push(c);
  }
  if (matches.length === 0) return null;
  return Math.min(...matches);
}

function offsetAt(epochMin: number, tz: string): number {
  const p = zonedParts(epochMin, tz);
  const bits = p.date.split("-");
  const y = Number(bits[0]);
  const mo = Number(bits[1]);
  const d = Number(bits[2]);
  const localAsUtc = Math.floor(
    Date.UTC(y, mo - 1, d, Math.floor(p.minutes / 60), p.minutes % 60) / 60_000,
  );
  return localAsUtc - epochMin;
}

function formatLabel(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const am = h < 12;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${am ? "AM" : "PM"}`;
}
